// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import {
  deleteChronicIntervention,
  updateChronicIntervention,
  deleteAcuteIntervention,
  updateAcuteIntervention,
} from '../../../actions/treatmentActions';

import DateText from '../../DateText.jsx';
import Table from '../../Table.jsx';
import Typeahead from '../../Typeahead.jsx';

type Props = {
  activePatientId: string,
  chronicInterventions: Object[],
  acuteInterventions: Object[],
  problemIdToProblem: Object,
  deleteChronicIntervention: Function,
  updateChronicIntervention: Function,
  deleteAcuteIntervention: Function,
  updateAcuteIntervention: Function,
  current: boolean,
  resolved: boolean,
  acute: boolean,
  chronic: boolean,
}

class InterventionTableComponent extends Component<Props> {
  /* eslint-disable no-param-reassign */
  transformDates(formValues) {
    formValues.startDate = moment(formValues.startDate, 'DD-MM-YYYY').toISOString();
    formValues.endDate = moment(formValues.endDate, 'DD-MM-YYYY').toISOString();
  }

  handleDeleteRow = (id: string, row: Object) => {
    if (this.props.chronic || row.chronic) {
      this.props.deleteChronicIntervention(this.props.activePatientId, id);
    } else if (this.props.acute || row.acute) {
      this.props.deleteAcuteIntervention(this.props.activePatientId, id);
    }
  }

  handleSaveRow = (id: string, formData: Object, row: Object) => {
    this.transformDates(formData.values);
    if (this.props.chronic || row.chronic) {
      this.props.updateChronicIntervention(this.props.activePatientId,
        {
          treatmentId: id,
          ...formData.values,
        });
    } else if (this.props.acute || row.acute) {
      this.props.updateAcuteIntervention(this.props.activePatientId,
        {
          treatmentId: id,
          ...formData.values,
        });
    }
  }

  render() {
    const { problemIdToProblem } = this.props;
    const columns = [
      {
        Header: 'Start Date',
        id: 'startDate',
        cell: item => (item.startDate ? moment(item.startDate).format() : '--'),
        editCell: item => <DateText field='startDate' item={item} />,
        width: 100,
      },
      {
        Header: 'End Date',
        id: 'endDate',
        cell: item => (item.endDate ? moment(item.endDate).format() : '(ongoing)'),
        editCell: item => <DateText field="endDate" item={item} nullable />,
        width: 100,
      },
      {
        Header: 'Name',
        id: 'name',
        editCell: item => <Typeahead field='name' initialValue={item.name} endpoint='intervention' />,
      },
      {
        Header: 'Diagnosis',
        id: 'diagnosis',
        cell: (item) => {
          if (item.problemId in problemIdToProblem && problemIdToProblem[item.problemId].diagnosis) {
            return problemIdToProblem[item.problemId].diagnosis;
          }
          return '--';
        },
      },
      {
        Header: 'Diagnosis Date',
        id: 'diagnosisDate',
        cell: (item) => {
          if (item.problemId in problemIdToProblem && problemIdToProblem[item.problemId].onsetDate) {
            return moment(problemIdToProblem[item.problemId].onsetDate).format();
          }
          return '--';
        },
        width: 120,
      },
    ];

    let interventions = [];
    let { acuteInterventions, chronicInterventions } = this.props;
    if (this.props.acute) {
      interventions = acuteInterventions;
    } else if (this.props.chronic) {
      interventions = chronicInterventions;
    } else {
      acuteInterventions = acuteInterventions.map(intervention => ({ ...intervention, acute: true }));
      chronicInterventions = chronicInterventions.map(intervention => ({ ...intervention, chronic: true }));
      interventions = acuteInterventions.concat(chronicInterventions);
    }

    if (this.props.current) {
      interventions = interventions.filter(intervention => !intervention.endDate || moment(intervention.endDate) > moment());
    } else if (this.props.resolved) {
      interventions = interventions.filter(intervention => intervention.endDate && moment(intervention.endDate) <= moment());
    }

    interventions = interventions.sort((a, b) => {
      if (a.endDate && !b.endDate) {
        return 1;
      }
      if (b.endDate && !a.endDate) {
        return -1;
      }
      if (!a.startDate) {
        return 1;
      }
      if (!b.startDate) {
        return -1;
      }
      if (a.startDate < b.startDate) {
        return 1;
      }
      if (b.startDate < a.startDate) {
        return -1;
      }
      return 0;
    });

    return (
      <Table
        columns={columns}
        data={interventions}
        handleDeleteRow={this.handleDeleteRow}
        handleSaveRow={this.handleSaveRow}
      />
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  chronicInterventions: state.patient.data.chronicInterventions,
  acuteInterventions: state.patient.data.acuteInterventions,
  problemIdToProblem: state.patient.data.problemIdToProblem,
});

const mapDispatchToProps = dispatch => ({
  deleteChronicIntervention:
    (patientId, interventionId) => dispatch(deleteChronicIntervention(patientId, interventionId)),
  updateChronicIntervention:
    (patientId, intervention) => dispatch(updateChronicIntervention(patientId, intervention)),
  deleteAcuteIntervention:
    (patientId, interventionId) => dispatch(deleteAcuteIntervention(patientId, interventionId)),
  updateAcuteIntervention:
    (patientId, intervention) => dispatch(updateAcuteIntervention(patientId, intervention)),
});

const InterventionTable = connect(
  mapStateToProps,
  mapDispatchToProps,
)(InterventionTableComponent);

export default InterventionTable;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/treatment/InterventionTable.jsx